// React
import * as React from 'react';
import { createRef, useContext, useEffect, useState } from 'react';
// Microsoft
import {
  People,
  Person,
  PersonCardInteraction,
  PersonViewType,
} from '@microsoft/mgt-react';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
// Utils
import {
  callCommerceFunction,
  callFunction,
  loadContactData,
  SendTeamsNotification,
} from '../../lib/Helpers';
// Components
import { SubsUsersTable } from '../SubsUsersTable';
import { AppObject } from '../AppCard';
// Styling
import {
  Flex,
  Image,
  Text,
  Divider,
  CardFooter,
  Alert,
  InfoIcon,
} from '@fluentui/react-northstar';
import {
  Icon,
  MessageBar,
  MessageBarType,
  SpinnerSize,
  Theme,
} from '@fluentui/react';
import { Panel } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  SubscriptionCardContainer,
  SubscriptionCardContent,
  SubscriptionCardHeader,
  SubscriptionPanelFooter,
} from './styles';
import { Tag } from '../Tag';
import { CheckSquareOffset } from '@phosphor-icons/react';
import { BitButton } from '../BitButton';
import { Spinner } from '@fluentui/react';
import { TeamsFxContext } from '../../Context';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

type SubscriptionCardProps = {
  subscription?: SubscriptionObject;
  apps?: AppObject[];
  currentUser: string;
  tid: string;
  theme?: Theme;
  themeName?: string;
  onReloadRequest: React.Dispatch<void>;
  onUpgrade: React.Dispatch<void>;
};

export type EntitlementsObject = {
  appid: string;
  type: string;
  unit: string;
  value: string;
};

export type PlanObject = {
  planId: string;
  displayName: string;
  isPrivate: boolean;
  entitlements: EntitlementsObject[];
};

export type TermObject = {
  startDate: string;
  endDate: string;
  termUnit: string;
};

export type SubscriptionObject = {
  id: string;
  name: string;
  img: string;
  publisherId: string;
  planId: PlanObject;
  quantity: string;
  beneficiary: string;
  isFreeTrial: boolean;
  isTest: boolean;
  saasSubscriptionStatus: string;
  term: TermObject;
  adminUsers: string[];
  users: string[];
};

const SubscriptionCard = ({
  subscription,
  apps,
  currentUser,
  tid,
  theme,
  themeName,
  onReloadRequest,
  onUpgrade,
}: SubscriptionCardProps) => {
  const appInsights = useAppInsightsContext();

  const { t } = useTranslation('settings');

  const { teamsUserCredential, locale } = useContext(TeamsFxContext);

  const [savedAdmins, setSavedAdmins] = useState<string[]>(
    subscription?.adminUsers as string[]
  );
  const [savedUsers, setSavedUsers] = useState<string[]>(
    subscription?.users as string[]
  );
  const [admins, setAdmins] = useState<string[]>(
    subscription?.adminUsers as string[]
  );

  const [users, setUsers] = useState<string[]>(subscription?.users as string[]);

  const [toNotifyUsers, setNotifyUsers] = useState<string[]>([]);
  const userPeopleRef = createRef();

  const userIsAdmin: boolean = subscription?.adminUsers.find(
    (value) => value === currentUser
  )
    ? true
    : false;

  const userIsOwner: boolean =
    subscription?.beneficiary === currentUser ? true : false;

  const onPanelCloseClicked = () => {
    setAdmins(savedAdmins);
    setUsers(savedUsers);
    dismissPanel();
  };

  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] =
    useBoolean(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (savingError) {
      toast(t('SUBSCRIPTIONS_SAVING_ERROR') + savingError, { type: 'error' });
    }
  }, [savingError, t]);

  const onRenderFooterContent = React.useCallback(() => {
    const onSaveClicked = () => {
      if (isDirty) {
        setIsSaving(true);
        loadContactData(users)
          .then((conts) => {
            const data: any = {
              subid: subscription?.id,
              updateType: 'multiple',
              users: users,
              adminUsers: admins,
              contacts: conts.map((value) => {
                return {
                  name: value.displayName,
                  emailId: value.mail,
                  objectId: value.id,
                  tenantId: tid,
                };
              }),
            };
            callFunction(
              true,
              'patchSubscriptionUsers',
              JSON.stringify(data),
              false,
              teamsUserCredential
            )
              .then((data) => {
                onReloadRequest();
                setSavedAdmins(admins);
                setSavedUsers(users);
                setSavingError(undefined);
                setIsSaving(false);
                if (toNotifyUsers) {
                  toNotifyUsers.forEach((element) => {
                    if (element !== currentUser)
                      SendTeamsNotification(
                        element,
                        subscription?.name as string
                      ).catch((error) => {
                        if (process.env.REACT_APP_APP_INSIGHTS !== '')
                          appInsights.trackException({
                            exception: error,
                            properties: {
                              component: 'SubscriptionCard',
                              location: 'onSendTeamsNotification',
                            },
                          });
                      });
                  });
                }
                dismissPanel();
              })
              .catch((error) => {
                if (process.env.REACT_APP_APP_INSIGHTS !== '')
                  appInsights.trackException({
                    exception: error,
                    properties: {
                      component: 'SubscriptionCard',
                      location: 'onPatchSubscriptionUsers',
                    },
                  });
                setSavingError(error);
                setIsSaving(false);
                dismissPanel();
              });
          })
          .catch((error) => {
            if (process.env.REACT_APP_APP_INSIGHTS !== '')
              appInsights.trackException({
                exception: error,
                properties: {
                  component: 'SubscriptionCard',
                  location: 'onLoadContactData',
                },
              });
          });
      } else {
        dismissPanel();
      }
    };

    const onCancelClicked = () => {
      setAdmins(savedAdmins);
      setUsers(savedUsers);
      dismissPanel();
    };

    return (
      <SubscriptionPanelFooter>
        <BitButton label={t('FOOTER_SAVE_BUTTON')} action={onSaveClicked} />
        <BitButton
          label={t('FOOTER_CANCEL_BUTTON')}
          variant="secondary"
          action={onCancelClicked}
        />
        {isSaving && <Spinner />}
      </SubscriptionPanelFooter>
    );
  }, [
    admins,
    appInsights,
    currentUser,
    dismissPanel,
    isDirty,
    isSaving,
    onReloadRequest,
    savedAdmins,
    savedUsers,
    subscription?.id,
    subscription?.name,
    t,
    teamsUserCredential,
    tid,
    toNotifyUsers,
    users,
  ]);

  const [isActivating, setIsActivating] = useState(false);

  function activateSubscription(
    subid: string,
    planid: string,
    quantity: string
  ) {
    setIsActivating(true);
    callCommerceFunction(
      true,
      'activateSubscription?{0}&subid=' +
        subid +
        '&planid=' +
        planid +
        '&quantity=' +
        quantity
    )
      .catch((error) => {
        toast.error(t('SUBSCRIPTIONS_ACTIVATION_ERROR'));
        if (appInsights)
          appInsights.trackException({
            exception: error,
            properties: {
              component: 'App',
              location: 'onActivateSubscription',
            },
          });
      })
      .then(() => {
        onReloadRequest();
      })
      .finally(() => {
        setIsActivating(false);
      });
  }

  return (
    <SubscriptionCardContainer>
      {subscription?.isFreeTrial && (
        <MessageBar
          hidden={!subscription?.isFreeTrial}
          messageBarType={MessageBarType.info}
          isMultiline={false}
        >
          {t('TRIAL_MESSAGE_BAR')}
        </MessageBar>
      )}
      <SubscriptionCardHeader>
        <div className="planTitle" id="step-current-subscription">
          <Image src={subscription?.img} fluid />
          <h1>
            {subscription?.planId.displayName}
            {subscription?.saasSubscriptionStatus.trim() === 'Subscribed' ? (
              <Tag color="green" content="Active" variant="outlined" />
            ) : (
              <Tag color="orange" content="Pending" variant="outlined" />
            )}
          </h1>
          <h2>{subscription?.name}</h2>
        </div>
        <div className="planInfo" id="step-subscription-data">
          <div className="expiration">
            <h2>{t('PLAN_EXPIRES_ON')}</h2>
            <p>
              {subscription?.term.endDate
                ? new Date(subscription?.term.endDate).toLocaleDateString(
                    locale
                  )
                : t('PLAN_NOT_ACTIVE')}
            </p>
          </div>
          <div className="owner">
            <h2>{t('PLAN_OWNER_TITLE')}</h2>
            <Person
              className={themeName !== 'default' ? 'mgt-dark' : ''}
              userId={subscription?.beneficiary}
              showPresence
              view={PersonViewType.avatar}
              personCardInteraction={PersonCardInteraction.hover}
            />
          </div>
          <div className="status">
            {subscription?.isFreeTrial &&
              subscription?.saasSubscriptionStatus.trim() === 'Subscribed' &&
              userIsOwner && <BitButton label="Upgrade" action={onUpgrade} />}
            {subscription?.saasSubscriptionStatus.trim() ===
              'PendingFulfillmentStart' && (
              <button
                hidden={!userIsOwner}
                disabled={isActivating}
                className="bitbuttonsmall"
                onClick={() => {
                  appInsights.trackEvent({
                    name: 'Activate Clicked',
                    properties: {
                      subsName: subscription?.id,
                    },
                  });
                  activateSubscription(
                    subscription?.id,
                    subscription?.planId.planId,
                    subscription?.quantity
                  );
                }}
              >
                {isActivating && <Spinner size={SpinnerSize.small} />}
                {isActivating ? t('PLAN_IS_ACTIVATING') : t('PLAN_ACTIVATE')}
              </button>
            )}
          </div>
        </div>
      </SubscriptionCardHeader>

      <SubscriptionCardContent>
        <section id="step-subscription-apps">
          <h3>{t('SUBSCRIPTIONS_APPS_ENTITLEMENTS')}</h3>
          <div className="subscriptionsContainer">
            {subscription?.planId.entitlements.map(
              (ent) =>
                apps && (
                  <div className="subscriptionItem">
                    <Icon
                      iconName={
                        apps.find((value) => value.id === ent.appid)?.iconName
                      }
                    />
                    <h4>
                      {apps.find((value) => value.id === ent.appid)?.name}
                    </h4>
                    <p>{`${ent.type}: ${ent.value}`}</p>
                  </div>
                )
            )}
          </div>
        </section>
        <section>
          <h3>{t('SUBSCRIPTIONS_USERS_TITLE')}</h3>

          {userIsAdmin && !subscription?.isFreeTrial && (
            <>
              <BitButton
                label={t('SUBSCRIPTIONS_MANAGE_BUTTON')}
                icon={<CheckSquareOffset weight="duotone" />}
                action={openPanel}
              />
              <p>{t('SUBSCRIPTIONS_MANAGEMENT_INSTRUCTIONS')}</p>
            </>
          )}

          {savingError && (
            <Alert
              danger
              styles={{ fontSize: 'small', marginBottom: 5 }}
              icon={<InfoIcon data-builder-id={undefined} />}
            />
          )}
        </section>
        <section id="step-subscription-users">
          <h3>{t('SUBSCRIPTIONS_USERS')}</h3>
          <People
            className={themeName !== 'default' ? 'mgt-dark' : ''}
            ref={userPeopleRef}
            showPresence
            showMax={25}
            userIds={users}
          ></People>
        </section>
        <section id="step-subscription-admins">
          <h3>{t('SUBSCRIPTIONS_ADMINS')}</h3>
          <People
            className={themeName !== 'default' ? 'mgt-dark' : ''}
            showMax={25}
            showPresence
            userIds={admins}
          ></People>
        </section>
      </SubscriptionCardContent>

      <Divider content="" />
      <CardFooter>
        <Text
          content={t('SUBSCRIPTION_LICENCES')}
          size="small"
          weight="bold"
          style={{ marginBottom: 5 }}
        />
        <Text
          hidden={!userIsOwner || subscription?.isFreeTrial}
          content={t('SUBSCRIPTIONS_MANAGEMENT_DISCLAIMER')}
          size="small"
          style={{ marginBottom: 5 }}
        />
        <Flex style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Text
            content={`${subscription?.users.length} ${t(
              'SUBSCRIPTIONS_LICENCES_AMOUNT'
            )} ${subscription?.quantity} ${t(
              'SUBSCRIPTIONS_LICENCES_ASSIGNED'
            )}`}
            size="small"
            style={{ marginBottom: 5 }}
          />
          <Flex styles={{ height: '25px' }}>
            {/* <button hidden={!userIsOwner || subscription?.isFreeTrial} id='popbuttonsave' onClick={() => { appInsights.trackEvent({ name: "Change Licenses Clicked", properties: { subsName: subscription?.id } }); }} className='bitbuttonsmall'>Change</button> */}
          </Flex>
        </Flex>
      </CardFooter>
      <div className="panel">
        <Panel
          isOpen={isOpen}
          onDismiss={onPanelCloseClicked}
          headerText={t('PANEL_MANAGE_USERS_HEADER')}
          closeButtonAriaLabel="Close"
          onRenderFooterContent={onRenderFooterContent}
          isFooterAtBottom={true}
        >
          <SubsUsersTable
            users={users as string[]}
            admins={admins as string[]}
            owner={subscription?.beneficiary as string}
            maxUsers={+(subscription?.quantity as string)}
            themeName={themeName}
            teamsUserCredential={teamsUserCredential}
            onUsersChanged={setUsers}
            onAdminsChanged={setAdmins}
            onIsDirty={setIsDirty}
            onToNotifyUsersChanged={setNotifyUsers}
          />
        </Panel>
      </div>
    </SubscriptionCardContainer>
  );
};

export default SubscriptionCard;
